import { KeyboardEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";

// Reusable tag input component
export function TagInput({ tags, onChange, placeholder, inputValue, onInputChange }: {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  inputValue: string;
  onInputChange: (v: string) => void;
}) {
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!tags.includes(inputValue.trim())) {
        onChange([...tags, inputValue.trim()]);
      }
      onInputChange("");
    }
  };

  return (
    <div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {tags.map((t, i) => (
            <Badge key={i} variant="secondary" className="gap-1">
              {t}
              <button onClick={() => onChange(tags.filter((_, idx) => idx !== i))} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        placeholder={placeholder || "Escribe y presiona Enter"}
        value={inputValue}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

// Catalog-based tool multi-select component
export function ToolMultiSelect({ selected, onChange, catalogTools }: {
  selected: string[];
  onChange: (tools: string[]) => void;
  catalogTools: { name: string; vendor: string; category: string }[];
}) {
  const available = catalogTools.filter(t => !selected.includes(t.name));
  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map((name, i) => (
            <Badge key={i} variant="secondary" className="gap-1">
              {name}
              <button onClick={() => onChange(selected.filter((_, idx) => idx !== i))} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      )}
      {available.length > 0 && (
        <Select onValueChange={(v) => { if (v && !selected.includes(v)) onChange([...selected, v]); }}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar herramienta del catálogo" />
          </SelectTrigger>
          <SelectContent>
            {available.map(t => (
              <SelectItem key={t.name} value={t.name}>{t.name} — {t.vendor}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// Data source multi-select component
export function DataSourceMultiSelect({ selected, onChange, dataSources }: {
  selected: string[];
  onChange: (sources: string[]) => void;
  dataSources: { name: string }[];
}) {
  const available = dataSources.filter(ds => !selected.includes(ds.name));
  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map((name, i) => (
            <Badge key={i} variant="secondary" className="gap-1">
              {name}
              <button onClick={() => onChange(selected.filter((_, idx) => idx !== i))} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      )}
      {available.length > 0 && (
        <Select onValueChange={(v) => { if (v && !selected.includes(v)) onChange([...selected, v]); }}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar fuente de datos" />
          </SelectTrigger>
          <SelectContent>
            {available.map(ds => (
              <SelectItem key={ds.name} value={ds.name}>{ds.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {available.length === 0 && selected.length === 0 && (
        <p className="text-sm text-muted-foreground">No hay fuentes de datos registradas</p>
      )}
    </div>
  );
}

// Project multi-select for linking projects to solutions
export function ProjectMultiSelect({ selected, onChange, projects }: {
  selected: string[];
  onChange: (ids: string[]) => void;
  projects: any[];
}) {
  const available = projects.filter(p => !selected.includes(p.id));
  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map((id) => {
            const proj = projects.find(p => p.id === id);
            return (
              <Badge key={id} variant="secondary" className="gap-1">
                {proj?.name || id}
                <button onClick={() => onChange(selected.filter(s => s !== id))} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
              </Badge>
            );
          })}
        </div>
      )}
      {available.length > 0 && (
        <Select onValueChange={(v) => { if (v && !selected.includes(v)) onChange([...selected, v]); }}>
          <SelectTrigger>
            <SelectValue placeholder="Vincular proyecto del portafolio" />
          </SelectTrigger>
          <SelectContent>
            {available.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} — {(p as any).profiles?.full_name || ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {available.length === 0 && selected.length === 0 && (
        <p className="text-sm text-muted-foreground">No hay proyectos disponibles en esta organización</p>
      )}
    </div>
  );
}

// Constants
export const TECHNOLOGY_OPTIONS = [
  "PostgreSQL", "MySQL", "SQL Server", "MongoDB", "Oracle", "Snowflake", "BigQuery",
  "Redshift", "Databricks", "S3", "Azure Blob", "GCS", "Kafka", "REST API", "GraphQL",
  "CSV/Excel", "Elasticsearch", "Redis", "DynamoDB", "Otro"
];

export const FRAMEWORK_OPTIONS = [
  "PyTorch", "TensorFlow", "Scikit-learn", "Hugging Face", "LangChain",
  "OpenAI API", "Azure ML", "SageMaker", "Vertex AI", "Keras", "ONNX", "Otro"
];

export const licenseTypeLabels: Record<string, string> = {
  per_user: 'Por usuario',
  enterprise: 'Enterprise',
  freemium: 'Freemium',
  open_source: 'Open Source',
};

export const modelStatusLabels: Record<string, string> = {
  development: 'Desarrollo',
  testing: 'Testing',
  production: 'Producción',
  retired: 'Retirado',
};

export const softwareCategories_options = [
  { value: "ERP", label: "ERP" },
  { value: "CRM", label: "CRM" },
  { value: "HCM", label: "HCM" },
  { value: "SCM", label: "SCM" },
  { value: "BI", label: "BI / Analytics" },
  { value: "Colaboración", label: "Colaboración" },
  { value: "Finanzas", label: "Finanzas" },
  { value: "Otro", label: "Otro" },
];

export const statusLabels: Record<string, string> = {
  ideation: 'Ideación',
  in_progress: 'En progreso',
  submitted: 'Enviado',
  completed: 'Completado',
};

// Helper functions
export const getDataSourceIcon = (type: string) => {
  const icons: Record<string, string> = {
    database: "Server", file: "HardDrive", api: "Layers",
    data_lake: "Database", data_warehouse: "Database", streaming: "Layers",
    cloud_storage: "Cloud", spreadsheet: "Sheet", unstructured: "FileText",
    social_media: "Globe", iot: "Cpu", saas: "Cloud", email: "Mail",
  };
  return icons[type] || "Database";
};

export const getDataSourceTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    database: "Base de Datos", file: "Archivos", api: "API",
    data_lake: "Data Lake", data_warehouse: "Data Warehouse", streaming: "Streaming",
    cloud_storage: "Cloud Storage", spreadsheet: "Hoja de Cálculo", unstructured: "No Estructurado",
    social_media: "Redes Sociales", iot: "IoT / Sensores", saas: "SaaS / App", email: "Email / Mensajería",
  };
  return labels[type] || type;
};
